import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useZendeskLocalization } from '@repo/poly-hooks/zendesk-localization';
import { fetchArticleJsonFromS3, getZendeskArticleUrl } from '#utils/zendesk';
import { useSentry } from '#sentry';
import { isNetworkError } from '#utils/is-network-error';

/**
 * Shared hook for Zendesk article fetching.
 *
 * This hook:
 * - Fetches the S3 JSON data (country - article ID mapping)
 * - Gets service data (country, help center URL)
 * - Handles Sentry error reporting
 * - Extracts URL from S3 data or falls back to local config
 *
 * @param articleSlug - The article slug
 * @returns string - The article URL
 *
 */
export function useZendeskArticle(articleSlug: string): string {
  const sentry = useSentry();
  const zendesk = useZendeskLocalization();

  const { data, error } = useQuery({
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    queryKey: ['zendesk-article-s3-json', articleSlug],
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    queryFn: () => fetchArticleJsonFromS3(articleSlug),
    staleTime: Infinity,
  });

  const helpCenterUrl = zendesk.getLocalizedHelpCenter;
  const organizationCountry = zendesk.organizationCountry;

  const url = useMemo(
    () =>
      getZendeskArticleUrl({
        articleSlug,
        helpCenterUrl,
        organizationCountry,
        s3Data: data,
      }),
    [articleSlug, helpCenterUrl, organizationCountry, data]
  );

  useEffect(() => {
    if (error && !isNetworkError(error)) {
      sentry.captureMessage(`Failed to fetch Zendesk article "${articleSlug}"`, {
        level: 'error',
        extra: {
          articleSlug,
          error,
          organizationCountry,
        },
      });
    }
  }, [articleSlug, error, organizationCountry, sentry]);

  return url;
}
